import React from 'react';
import {
    Box, Grid, Typography, Divider, Chip, Button, CircularProgress, Snackbar, Alert,
    FormControlLabel, Checkbox, Tooltip, Paper, TextField
} from '@mui/material';
import { Search, Clear, FilterAlt, RemoveRedEye } from '@mui/icons-material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { styled, alpha } from '@mui/material/styles';
import ExclusiveDatagrid from '../../../../components/ui/ExclusiveDatagrid';
import ResultDataGrid from './ResultDataGrid';

// Styled components
const StyledSearchBox = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
    }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1),
        transition: 'all 0.2s ease',
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        '&:hover': {
            backgroundColor: theme.palette.background.paper,
        },
        '&.Mui-focused': {
            backgroundColor: theme.palette.background.paper,
            boxShadow: '0 0 0 2px ${theme.palette.primary.main}',
        }
    },
    '& .MuiInputLabel-root': {
        transition: 'all 0.2s ease',
    },
    [theme.breakpoints.down('sm')]: {
        '& .MuiOutlinedInput-input': {
            padding: '14px 16px',
        }
    }
}));

const StyledCheckbox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    '& .MuiFormControlLabel-root': {
        marginRight: 0
    }
}));

const SearchChip = styled(Chip)(({ theme }) => ({
    margin: theme.spacing(0.5),
    borderRadius: theme.spacing(3),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
    '& .MuiChip-deleteIcon': {
        color: theme.palette.error.main,
        '&:hover': {
            color: theme.palette.error.dark,
        }
    }
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    padding: '8px 24px',
    textTransform: 'none',
    fontWeight: 600,
    transition: 'all 0.2s ease',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }
}));

const PirateCallSearch = ({
    searchFields,
    queryPool,
    searchMode,
    isSubmitting,
    loading,
    activeFilters,
    onInputChange,
    onCheckboxChange,
    onKeyPress,
    onSubmit,
    onClearAll,
    onRemoveFilter,
    onResultDetails,
    onFillFormFromResult,
    results,
    hasValidSearchCriteria
}) => {
    const isGsmMode = searchMode === 1; // SearchModeBase.TEL

    const columns = [
        { field: 'TC', headerName: 'TC', width: 150, primary: true },
        { field: 'ADI', headerName: 'Adı', width: 150 },
        { field: 'SOYADI', headerName: 'Soyadı', width: 150 },
        { field: 'DOGUMTARIHI', headerName: 'Doğum Tarihi', width: 150 },
        { field: 'NUFUSIL', headerName: 'Nüfus İl', width: 150 },
        { field: 'NUFUSILCE', headerName: 'Nüfus İlçe', width: 150 }
    ];

    return (
        <>
            <StyledSearchBox elevation={0}>
                <form onSubmit={onSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Search color="primary" sx={{ mr: 2 }} />
                                <Typography variant="h5" fontWeight="600">
                                    {isGsmMode ? 'Telefon Numarası ile Arama' : 'Arama Kriterleri'}
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />
                        </Grid>
                        {searchFields.map(field => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={field.id}>
                                <StyledTextField
                                    fullWidth
                                    label={field.label}
                                    variant="outlined"
                                    value={queryPool[field.id] || ''}
                                    onChange={e => onInputChange(field.id, e.target.value)}
                                    onKeyPress={onKeyPress}
                                    disabled={isSubmitting || (isGsmMode && field.id !== 'GSM')}
                                    InputProps={{
                                        startAdornment: React.cloneElement(field.icon, {
                                            sx: { color: 'primary.main', mr: 1 }
                                        }),
                                        ...(field.id === 'DOGUMTARIHI' && {
                                            placeholder: 'dd.mm.yyyy'
                                        })
                                    }}
                                />
                                <StyledCheckbox>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={queryPool[field.id + '_strict'] !== false}
                                                onChange={e => onCheckboxChange(field.id, e.target.checked)}
                                                disabled={isSubmitting || (isGsmMode && field.id !== 'GSM')}
                                            />
                                        }
                                        label="Tam Eşleşme"
                                    />
                                </StyledCheckbox>
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <Box 
                                display="flex" 
                                flexDirection={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between" 
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                gap={2}
                            >
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: 1,
                                    width: { xs: '100%', sm: 'auto' }
                                }}>
                                    {activeFilters.map(filter => (
                                        <SearchChip
                                            key={filter.field}
                                            label={`${filter.field}: ${filter.value}`}
                                            onDelete={() => onRemoveFilter(filter.field)}
                                            disabled={isSubmitting}
                                        />
                                    ))}
                                </Box>
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 2,
                                    width: { xs: '100%', sm: 'auto' }
                                }}>
                                    <StyledButton
                                        variant="outlined"
                                        color="error"
                                        startIcon={<Clear />}
                                        onClick={onClearAll}
                                        fullWidth={window.innerWidth < 600}
                                        disabled={isSubmitting}
                                    >
                                        Temizle
                                    </StyledButton>
                                    <StyledButton
                                        variant="contained"
                                        color="primary"
                                        startIcon={isSubmitting ? 
                                            <CircularProgress size={20} color="inherit" /> : 
                                            <Search />
                                        }
                                        type="submit"
                                        fullWidth={window.innerWidth < 600}
                                        disabled={isSubmitting || !hasValidSearchCriteria}
                                    >
                                        {isSubmitting ? 'Aranıyor...' : 'Ara'}
                                    </StyledButton>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </StyledSearchBox>
            {results && results.length > 0 && (
                <Box mt={3}>
                    <Box display="flex" alignItems="center" mb={3}>
                        <FilterAlt color="primary" sx={{ mr: 2 }} />
                        <Typography variant="h6" fontWeight="600">
                            Arama Sonuçları
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                            ({results.length} sonuç)
                        </Typography>
                    </Box>
                    <ResultDataGrid
                        columns={columns}
                        data={results}
                        onViewDetails={onResultDetails}
                        onFillFormFromResult={onFillFormFromResult}
                    />
                </Box>
            )}
        </>
    );
};

export default PirateCallSearch; 